// O patch de captura do subscription id do OneSignal — lógica PURA.
//
// POR QUE SAIU DE src/lib/oneSignal.ts (task 6cad8b58): este objeto é a regra
// ÚNICA dos TRÊS writers do id (persistSubscriptionSnapshot e saveOptInToDb em
// hooks/useOneSignal.ts, captureSubscriptionIdAtBoot em lib/oneSignal.ts), e
// morava num módulo que importa o SDK web do OneSignal, o Capacitor, o Sentry e
// o client do Supabase. Um contrato de ESCRITA NO BANCO compartilhado por três
// caminhos precisa ser afirmável por teste sem carregar nada disso — mesma
// razão que tirou `healthWindowStart` de dentro do `syncHealth` e a decisão do
// FitnessWorkout para `fitnessSetGuards`.
//
// Zero imports de propósito. Quem quebrar isso quebra o pino de
// src/test/pushCapturePatchSchema.test.ts, que importa este módulo direto.
//
// ⚠️ TODA chave devolvida aqui é NOME DE COLUNA de public.user_profiles. Não é
// convenção estética: as três vão num único `.update()`, e o PostgREST rejeita
// o objeto INTEIRO quando uma delas não existe no schema — o metadado ausente
// mata o dado. Foi o que aconteceu entre 12 e 14/08: captura de push caiu de
// ~61% para 0% porque `onesignal_subscription_captured_at` não existia no
// banco, e o `onesignal_subscription_id` foi junto na recusa. ~415 pessoas sem
// push. O pino existe para essa divergência nascer vermelha no CI em vez de
// virar "[object Object]" no Sentry duas semanas depois.

/**
 * Ciclo de vida completo do id de subscription:
 *   capturado   → o writer grava id + captured_at=agora + dead_at=NULL;
 *   morto       → agents-dispatch recebe "no recipients" do OneSignal e
 *                 carimba user_profiles.onesignal_subscription_dead_at;
 *                 dispatchers passam a tratar o perfil como sem-push (agents:
 *                 skipped_channel; comeback: fora dos candidatos) — para de
 *                 errar todo dia num alvo morto;
 *   recapturado → o usuário volta (reabre/reinstala/religa notificação), um
 *                 writer captura um id — IGUAL ou diferente do armazenado — e
 *                 este patch ZERA dead_at no MESMO write;
 *   vivo        → dispatchers voltam a entregar.
 *
 * O clear de `dead_at` é parte do contrato, não otimização: sem ele, quem
 * reinstalasse o app ficaria permanentemente sem push. Por isso TODO write de
 * id não-nulo monta o payload aqui, nunca em objeto avulso.
 */
export function subscriptionCapturePatch(id: string): Record<string, unknown> {
  return {
    onesignal_subscription_id: id,
    onesignal_subscription_captured_at: new Date().toISOString(),
    onesignal_subscription_dead_at: null,
  };
}

/**
 * Quais chaves do patch NÃO existem como coluna — o predicado do pino.
 *
 * Separado da asserção para poder ser exercitado por MUTAÇÃO: o teste alimenta
 * um patch sintético com chave inventada e exige que ela apareça aqui. Sem
 * isso, um pino quebrado passaria como "repo limpo" para sempre — mesma razão
 * pela qual pureUpdater.test.ts tem teste do próprio scanner.
 */
export function missingColumnsFor(
  patch: Record<string, unknown>,
  columns: Iterable<string>,
): string[] {
  const known = new Set(columns);
  return Object.keys(patch).filter((k) => !known.has(k)).sort();
}
